// vg-binding: CONTEXT:decisions  (P999.11.D-09/D-14/D-15/D-16 tokens + D-04 dark-ok)
// vg-binding: INTERFACE-STANDARDS:error-shape  (pure FE restyle — no API surface changed)
// apps/admin/src/pages/setup/SetupEnable2FAPage.tsx
//
// Phase 1 Task 48 — Setup Wizard Step 2: enroll TOTP via QR + verify code.
//
// Per CONTEXT (force enrollment, NO Skip button) + API-CONTRACTS
// POST /api/v1/setup/verify-2fa.
//
// Inputs:
//   - `setupTotpEnrollmentToken` (sessionStorage, written by Step 1)
//   - `setupOtpauthUri` (optional — if BE emitted it; else build a fallback
//     from `setupTotpSecret` + email)
//   - `setupTotpSecret` (optional — manual key fallback when QR can't be
//     scanned)
//
// On success → captures `recoveryToken` to sessionStorage, navigates to
// step 3.

import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import QRCode from 'qrcode';

import {
  Button,
  Form,
  FormControl,
  FormField,
  FormLabel,
  FormMessage,
  Input,
} from '@printway/ui';
import { resolveApiBaseUrl } from '@printway/shared/api';

import { useTranslation } from '../../i18n/useTranslation.js';
import { readSetupToken } from './SetupWizardLayout.js';

type TFunc = (key: string, vars?: Record<string, unknown>) => string;
import {
  SETUP_ENROLL_TOKEN_KEY,
  SETUP_OTPAUTH_URI_KEY,
  SETUP_TOTP_SECRET_KEY,
  SETUP_USER_EMAIL_KEY,
} from './SetupCreateAdminPage.js';

export const SETUP_RECOVERY_TOKEN_KEY = 'setupRecoveryToken';

// ---------------------------------------------------------------------------
// Schema
// ---------------------------------------------------------------------------

function buildVerify2faSchema(t: TFunc) {
  return z.object({
    totpCode: z
      .string()
      .regex(/^\d{6}$/, t('setup.enable2fa.validation.totpFormat')),
  });
}

type Verify2faFormValues = z.infer<ReturnType<typeof buildVerify2faSchema>>;

// ---------------------------------------------------------------------------
// API call — POST /api/v1/setup/verify-2fa
// ---------------------------------------------------------------------------

interface SetupVerify2faResponseData {
  ok: true;
  next: 'recovery-codes';
  recoveryToken: string;
}

interface ApiErrorEnvelope {
  success: false;
  error: { code: string; message: string };
}

async function callVerify2fa(
  totpEnrollmentToken: string,
  totpCode: string,
  setupToken: string,
  t: (key: string, vars?: Record<string, unknown>) => string,
): Promise<SetupVerify2faResponseData> {
  const baseUrl = resolveApiBaseUrl();
  const res = await fetch(`${baseUrl}/api/v1/setup/verify-2fa`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-Setup-Token': setupToken,
    },
    body: JSON.stringify({ totpEnrollmentToken, totpCode }),
  });

  const json = (await res.json().catch(() => null)) as
    | { success: true; data: SetupVerify2faResponseData }
    | ApiErrorEnvelope
    | null;

  if (!res.ok || !json || !('success' in json) || json.success !== true) {
    const err = json as ApiErrorEnvelope | null;
    const msg =
      err?.error?.message ?? t('setup.errors.httpFailed', { status: res.status });
    throw new Error(msg);
  }
  return json.data;
}

// ---------------------------------------------------------------------------
// Helpers — read step-1 hand-off + build otpauth URI fallback
// ---------------------------------------------------------------------------

interface EnrollmentHandoff {
  token: string | null;
  email: string | null;
  secret: string | null;
  otpauthUri: string | null;
}

function readEnrollmentHandoff(): EnrollmentHandoff {
  if (typeof window === 'undefined') {
    return { token: null, email: null, secret: null, otpauthUri: null };
  }
  try {
    return {
      token: window.sessionStorage.getItem(SETUP_ENROLL_TOKEN_KEY),
      email: window.sessionStorage.getItem(SETUP_USER_EMAIL_KEY),
      secret: window.sessionStorage.getItem(SETUP_TOTP_SECRET_KEY),
      otpauthUri: window.sessionStorage.getItem(SETUP_OTPAUTH_URI_KEY),
    };
  } catch {
    return { token: null, email: null, secret: null, otpauthUri: null };
  }
}

/**
 * If BE didn't emit an otpauth URI, build one from secret + email.
 * RFC 6238 / Google Authenticator format:
 *   otpauth://totp/Printway:{email}?secret={base32}&issuer=Printway
 */
function buildOtpauthUri(secret: string | null, email: string | null): string | null {
  if (!secret || !email) return null;
  const issuer = 'Printway';
  const label = encodeURIComponent(`${issuer}:${email}`);
  const params = new URLSearchParams({ secret, issuer });
  return `otpauth://totp/${label}?${params.toString()}`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function SetupEnable2FAPage(): JSX.Element {
  const { t } = useTranslation('ops');
  const navigate = useNavigate();
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [qrError, setQrError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [handoff, setHandoff] = useState<EnrollmentHandoff>(() =>
    readEnrollmentHandoff(),
  );

  const otpauthUri =
    handoff.otpauthUri ?? buildOtpauthUri(handoff.secret, handoff.email);

  // Generate QR data URL whenever otpauthUri changes.
  useEffect(() => {
    let cancelled = false;
    if (!otpauthUri) {
      setQrDataUrl(null);
      return;
    }
    QRCode.toDataURL(otpauthUri, { errorCorrectionLevel: 'M', margin: 1, width: 220 })
      .then((url) => {
        if (!cancelled) setQrDataUrl(url);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setQrError(
            err instanceof Error
              ? err.message
              : t('setup.enable2fa.qrError'),
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, [otpauthUri, t]);

  // Re-read sessionStorage if user navigates back/forward.
  useEffect(() => {
    setHandoff(readEnrollmentHandoff());
  }, []);

  const verify2faSchema = useMemo(() => buildVerify2faSchema(t), [t]);

  const form = useForm<Verify2faFormValues>({
    resolver: zodResolver(verify2faSchema as never),
    defaultValues: { totpCode: '' },
    mode: 'onSubmit',
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = form;

  const onSubmit = async ({ totpCode }: Verify2faFormValues): Promise<void> => {
    setSubmitError(null);

    const setupToken = readSetupToken();
    if (!setupToken) {
      setSubmitError(t('setup.enable2fa.errors.missingSetupToken'));
      return;
    }
    if (!handoff.token) {
      setSubmitError(t('setup.enable2fa.errors.missingEnrollToken'));
      return;
    }

    try {
      const data = await callVerify2fa(handoff.token, totpCode, setupToken, t);
      try {
        window.sessionStorage.setItem(
          SETUP_RECOVERY_TOKEN_KEY,
          data.recoveryToken,
        );
      } catch {
        // ignore
      }
      navigate('/setup/recovery-codes', { replace: true });
    } catch (err) {
      setSubmitError(
        err instanceof Error
          ? err.message
          : t('setup.enable2fa.errors.verifyFailed'),
      );
    }
  };

  return (
    <div>
      <h2 className="mb-1 text-lg font-semibold text-pw-text">
        {t('setup.enable2fa.heading')}
      </h2>
      <p className="mb-4 text-sm text-pw-text-muted">
        {t('setup.enable2fa.descriptionBefore')}
        <strong>{t('setup.enable2fa.descriptionRequired')}</strong>
        {t('setup.enable2fa.descriptionAfter')}
      </p>

      {/* QR + manual key */}
      <div className="mb-6 rounded-md border border-pw-border bg-pw-bg-muted p-4">
        <div className="flex flex-col items-center gap-3 sm:flex-row sm:items-start">
          <div className="flex h-[220px] w-[220px] items-center justify-center rounded bg-white" /* dark-ok: QR code surface must stay white to remain scannable (P999.11.D-04) */>
            {qrDataUrl ? (
              <img
                src={qrDataUrl}
                alt={t('setup.enable2fa.qrAlt')}
                width={220}
                height={220}
              />
            ) : (
              <span className="px-4 text-center text-xs text-pw-text-muted">
                {qrError ?? t('setup.enable2fa.qrGenerating')}
              </span>
            )}
          </div>
          <div className="flex-1 text-sm">
            <p className="font-medium text-pw-text">
              {t('setup.enable2fa.manualKeyLabel')}
            </p>
            <code className="mt-1 block break-all rounded bg-pw-bg px-2 py-1 font-mono text-xs text-pw-text">
              {handoff.secret ?? t('setup.enable2fa.manualKeyMissing')}
            </code>
            <p className="mt-3 text-xs text-pw-text-muted">
              {t('setup.enable2fa.accountLabel', {
                email: handoff.email ?? t('setup.enable2fa.emailUnknown'),
              })}
            </p>
            <p className="mt-1 text-xs text-pw-text-muted">
              {t('setup.enable2fa.issuerLabel')} <strong>Printway</strong>
            </p>
          </div>
        </div>
      </div>

      <Form onSubmit={handleSubmit(onSubmit)} noValidate>
        <FormField>
          <FormLabel htmlFor="setup-totp-code">
            {t('setup.enable2fa.totpCodeLabel')}
          </FormLabel>
          <FormControl>
            <Input
              id="setup-totp-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              pattern="\d{6}"
              placeholder="123456"
              aria-invalid={errors.totpCode ? 'true' : 'false'}
              {...register('totpCode')}
            />
          </FormControl>
          {errors.totpCode && <FormMessage>{errors.totpCode.message}</FormMessage>}
        </FormField>

        {submitError && (
          <div
            role="alert"
            className="rounded-md border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/40 p-3 text-sm text-red-700 dark:text-red-300"
          >
            {submitError}
          </div>
        )}

        <div className="flex justify-end pt-2">
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting
              ? t('setup.enable2fa.submitting')
              : t('setup.enable2fa.submit')}
          </Button>
        </div>
      </Form>
    </div>
  );
}
